import apiClient from "./apiClient";

// Define interfaces for the response types
export interface PhoneNumber {
  number: string;
  isBlocked: boolean;
  apiKeyId: number;
  blockReason?: string;
  blockedAt?: Date;
}

export interface EmailAddress {
  address: string;
  isBlocked: boolean;
  apiKeyId: number;
  blockReason?: string;
  blockedAt?: Date;
}

export interface EmailListResponse {
  totalCount: number;
  emailAddresses: EmailAddress[];
}

export interface PhoneListResponse {
  totalCount: number;
  phoneNumbers: PhoneNumber[];
}

export interface ListParams {
  sortBy?: string;
  sortDescending?: boolean;
  pageNumber?: number;
  pageSize?: number;
  apiKeyId?: number;
  phoneNumbers?: string;
  emailAddresses?: string;
  fullName?: string;
  status?: string;
  isBlocked?: boolean;
  isSubscribed?: boolean;
  blockReason?: string;
  blockedFrom?: string;
  blockedTo?: string;
  createdFrom?: string;
  createdTo?: string;
}

export const CommunicationService = {
  phoneNumbers: {
    getPhoneNumbers: (customerId: number | null, params: ListParams = {}) => {
      const queryParams: Record<string, string | boolean | number> = {
        sortDescending: params.sortDescending ?? false,
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 10,
      };
      if (customerId) queryParams.customerId = customerId;
      if (params.apiKeyId) queryParams.apiKeyId = params.apiKeyId;
      if (params.sortBy) queryParams.sortBy = params.sortBy;
      if (params.phoneNumbers) queryParams.phoneNumbers = params.phoneNumbers;
      if (params.fullName) queryParams.fullName = params.fullName;
      if (params.blockReason) queryParams.blockReason = params.blockReason;
      if (params.status) queryParams.status = params.status;
      if (params.createdFrom) queryParams.createdFrom = params.createdFrom;
      if (params.createdTo) queryParams.createdTo = params.createdTo;
      return apiClient.get<PhoneListResponse>(`api/v1/request-analytics/list/phone-numbers`, {
        params: queryParams,
      });
    },

    exportPhoneNumbers: (customerId: number | null, params: Pick<ListParams, "sortBy" | "sortDescending" | "apiKeyId" | "phoneNumbers" | "fullName" | "blockReason" | "status" | "createdFrom" | "createdTo"> = {}, exportFormat: string = "csv") => {
      const queryParams: Record<string, string | boolean | number> = {
        sortDescending: params.sortDescending ?? false,
        exportTo: exportFormat,
      };
      if (customerId) queryParams.customerId = customerId;
      if (params.apiKeyId) queryParams.apiKeyId = params.apiKeyId;
      if (params.sortBy) queryParams.sortBy = params.sortBy;
      if (params.phoneNumbers) queryParams.phoneNumbers = params.phoneNumbers;
      if (params.fullName) queryParams.fullName = params.fullName;
      if (params.blockReason) queryParams.blockReason = params.blockReason;
      if (params.status) queryParams.status = params.status;
      if (params.createdFrom) queryParams.createdFrom = params.createdFrom;
      if (params.createdTo) queryParams.createdTo = params.createdTo;
      return apiClient.get(`api/v1/request-analytics/list/phone-numbers`, {
        params: queryParams,
        responseType: "blob",
      });
    },

    updateBlockStatus: (
      data: {
        phoneNumber: string;
        shouldBlock: boolean;
        reason: string;
      },
      apiKey: string,
    ) => {
      return apiClient.put(
        "api/v1/sms/update-block-status",
        data,
        {
          headers: {
            "Content-Type": "application/json-patch+json",
            "X-API-Key": apiKey,
          },
        }
      );
    },
  },

  emailAddresses: {
    getEmailAddresses: (customerId: number | null, params: ListParams = {}) => {
      const queryParams: Record<string, string | boolean | number> = {
        sortDescending: params.sortDescending ?? false,
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 10,
      };
      if (customerId) queryParams.customerId = customerId;
      if (params.apiKeyId) queryParams.apiKeyId = params.apiKeyId;
      if (params.sortBy) queryParams.sortBy = params.sortBy;
      if (params.emailAddresses) queryParams.emailAddresses = params.emailAddresses;
      if (params.fullName) queryParams.fullName = params.fullName;

      if (params.isBlocked !== undefined) queryParams.isBlocked = params.isBlocked;
      if (params.isSubscribed !== undefined) queryParams.isSubscribed = params.isSubscribed;
      if (params.blockReason) queryParams.blockReason = params.blockReason;
      if (params.blockedFrom) queryParams.blockedFrom = params.blockedFrom;
      if (params.blockedTo) queryParams.blockedTo = params.blockedTo;
      if (params.createdFrom) queryParams.createdFrom = params.createdFrom;
      if (params.createdTo) queryParams.createdTo = params.createdTo;
      return apiClient.get<EmailListResponse>(`api/v1/request-analytics/list/email-addresses`, {
        params: queryParams,
      });
    },

    exportEmailAddresses: (customerId: number | null, params: Pick<ListParams, "sortBy" | "sortDescending" | "apiKeyId" | "emailAddresses" | "fullName" | "isBlocked" | "isSubscribed" | "blockReason" | "blockedFrom" | "blockedTo" | "createdFrom" | "createdTo"> = {}, exportFormat: string = "csv") => {
      const queryParams: Record<string, string | boolean | number> = {
        sortDescending: params.sortDescending ?? false,
        exportTo: exportFormat,
      };
      if (customerId) queryParams.customerId = customerId;
      if (params.apiKeyId) queryParams.apiKeyId = params.apiKeyId;
      if (params.sortBy) queryParams.sortBy = params.sortBy;
      if (params.emailAddresses) queryParams.emailAddresses = params.emailAddresses;
      if (params.fullName) queryParams.fullName = params.fullName;
      if (params.isBlocked !== undefined) queryParams.isBlocked = params.isBlocked;
      if (params.isSubscribed !== undefined) queryParams.isSubscribed = params.isSubscribed;
      if (params.blockReason) queryParams.blockReason = params.blockReason;
      if (params.blockedFrom) queryParams.blockedFrom = params.blockedFrom;
      if (params.blockedTo) queryParams.blockedTo = params.blockedTo;
      if (params.createdFrom) queryParams.createdFrom = params.createdFrom;
      if (params.createdTo) queryParams.createdTo = params.createdTo;
      return apiClient.get(`api/v1/request-analytics/list/email-addresses`, {
        params: queryParams,
        responseType: "blob",
      });
    },

    updateBlockStatus: (
      data: {
        email: string;
        shouldBlock: boolean;
        reason: string;
      },
      apiKey: string,
    ) => {
      return apiClient.put(
        "api/v1/emails/update-block-status",
        data,
        {
          headers: {
            "Content-Type": "application/json-patch+json",
            "X-API-Key": apiKey,
          },
        }
      );
    },
  },
};
